import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminNav from '../../components/admin/AdminNav';

const AdminDashboard = ({ backendUrl }) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalInspections: 0,
    pendingApprovals: 0,
    recentActivity: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Feature flags state
  const [featureFlags, setFeatureFlags] = useState({
    leaderboard_enabled: true,
    maps_enabled: true,
    inbox_enabled: true
  });
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [flagsUpdating, setFlagsUpdating] = useState({});
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  useEffect(() => {
    fetchDashboardStats();
    fetchFeatureFlags();
  }, []);

  // Fetch feature flags from backend
  const fetchFeatureFlags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/feature-flags`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      if (data.success && data.flags) {
        setFeatureFlags(data.flags);
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    } finally {
      setFlagsLoading(false);
    }
  };

  // Toggle a feature flag
  const toggleFeatureFlag = async (flagName) => {
    const newValue = !featureFlags[flagName];
    setFlagsUpdating(prev => ({ ...prev, [flagName]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/feature-flags/${flagName}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ enabled: newValue })
      });
      
      const data = await response.json();
      if (data.success) {
        setFeatureFlags(prev => ({ ...prev, [flagName]: newValue }));
      } else {
        console.error('Failed to update feature flag:', data);
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
    } finally {
      setFlagsUpdating(prev => ({ ...prev, [flagName]: false }));
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // Fetch admin stats from backend
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/stats`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      setStats({
        totalUsers: data.total_users || 0,
        totalInspections: data.total_inspections || 0,
        pendingApprovals: data.pending_count || 0,
        recentActivity: data.recent_inspections || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, link, color }) => (
    <Link
      to={link}
      className="block bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 hover:bg-[var(--pbfs-navy)]/80 hover:border-[var(--pbfs-green)]/30 transition-all duration-300"
    >
      <div>
        <p className="text-white/60 text-sm mb-2">{title}</p>
        <p className={`text-4xl font-bold ${color || 'text-white'}`}>
          {loading ? '...' : value.toLocaleString()}
        </p>
      </div>
    </Link>
  );

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />

      <div className="pt-6 px-4 max-w-full">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">Admin Dashboard</h1>
          <p className="text-white/60 text-xs sm:text-sm">Overview of TagQuest system status</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            link="/admin/users"
            color="text-[var(--pbfs-blue)]"
          />
          <StatCard
            title="Total Scans"
            value={stats.totalInspections}
            link="/admin/approvals"
            color="text-[var(--pbfs-green)]"
          />
          <StatCard
            title="Pending Tag Leads"
            value={stats.pendingApprovals}
            link="/admin/approvals"
            color="text-yellow-400"
          />
          <StatCard
            title="Recent Activity (7d)"
            value={stats.recentActivity}
            link="/admin/analytics"
            color="text-[var(--pbfs-blue)]"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/approvals"
              className="bg-white/5 hover:bg-[var(--pbfs-green)]/10 border border-white/10 hover:border-[var(--pbfs-green)]/30 rounded-lg p-4 transition-all duration-300"
            >
              <p className="font-semibold text-white mb-1">Review Tag Leads</p>
              <p className="text-sm text-white/60">Approve/reject tag submissions</p>
            </Link>

            <Link
              to="/admin/emails"
              className="bg-white/5 hover:bg-[var(--pbfs-blue)]/10 border border-white/10 hover:border-[var(--pbfs-blue)]/30 rounded-lg p-4 transition-all duration-300"
            >
              <p className="font-semibold text-white mb-1">Send Email Digest</p>
              <p className="text-sm text-white/60">Daily lead summary</p>
            </Link>

            <Link
              to="/admin/analytics"
              className="bg-white/5 hover:bg-[var(--pbfs-green)]/10 border border-white/10 hover:border-[var(--pbfs-green)]/30 rounded-lg p-4 transition-all duration-300"
            >
              <p className="font-semibold text-white mb-1">View Reports</p>
              <p className="text-sm text-white/60">Analytics & exports</p>
            </Link>
          </div>
        </div>

        {/* Feature Toggles - Collapsible */}
        <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl mb-8 overflow-hidden">
          {/* Collapsible Header */}
          <button
            onClick={() => setFeaturesExpanded(!featuresExpanded)}
            className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎛️</span>
              <div className="text-left">
                <h2 className="text-xl font-bold">Feature Toggles</h2>
                <p className="text-white/50 text-sm">Control user-facing features</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Quick status indicators */}
              <div className="flex gap-2">
                <span className={`w-2 h-2 rounded-full ${featureFlags.leaderboard_enabled ? 'bg-[var(--pbfs-green)]' : 'bg-gray-500'}`} title="Leaderboard" />
                <span className={`w-2 h-2 rounded-full ${featureFlags.maps_enabled ? 'bg-[var(--pbfs-green)]' : 'bg-gray-500'}`} title="Maps" />
                <span className={`w-2 h-2 rounded-full ${featureFlags.inbox_enabled ? 'bg-[var(--pbfs-green)]' : 'bg-gray-500'}`} title="Inbox" />
              </div>
              <span className={`text-white/60 transition-transform duration-300 ${featuresExpanded ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </div>
          </button>
          
          {/* Collapsible Content */}
          <div className={`transition-all duration-300 ease-in-out ${featuresExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
            <div className="px-6 pb-6 space-y-3">
              {/* Leaderboard Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xl">🏆</span>
                  <div>
                    <p className="font-semibold text-white">Leaderboard / Ranks</p>
                    <p className="text-sm text-white/50">User ranking page and dropdown button</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFeatureFlag('leaderboard_enabled')}
                  disabled={flagsLoading || flagsUpdating.leaderboard_enabled}
                  className={`relative w-12 h-7 rounded-full transition-all duration-300 flex-shrink-0 ${
                    featureFlags.leaderboard_enabled
                      ? 'bg-[var(--pbfs-green)]'
                      : 'bg-gray-600'
                  } ${flagsUpdating.leaderboard_enabled ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                >
                  <span 
                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      featureFlags.leaderboard_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Maps Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xl">🗺️</span>
                  <div>
                    <p className="font-semibold text-white">Maps / Explore Map</p>
                    <p className="text-sm text-white/50">User map page and dropdown button</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFeatureFlag('maps_enabled')}
                  disabled={flagsLoading || flagsUpdating.maps_enabled}
                  className={`relative w-12 h-7 rounded-full transition-all duration-300 flex-shrink-0 ${
                    featureFlags.maps_enabled
                      ? 'bg-[var(--pbfs-green)]'
                      : 'bg-gray-600'
                  } ${flagsUpdating.maps_enabled ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                >
                  <span 
                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      featureFlags.maps_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Inbox Toggle */}
              <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xl">📬</span>
                  <div>
                    <p className="font-semibold text-white">Inbox</p>
                    <p className="text-sm text-white/50">User notification inbox in bottom nav</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFeatureFlag('inbox_enabled')}
                  disabled={flagsLoading || flagsUpdating.inbox_enabled}
                  className={`relative w-12 h-7 rounded-full transition-all duration-300 flex-shrink-0 ${
                    featureFlags.inbox_enabled
                      ? 'bg-[var(--pbfs-green)]'
                      : 'bg-gray-600'
                  } ${flagsUpdating.inbox_enabled ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                >
                  <span 
                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
                      featureFlags.inbox_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              
              <p className="text-white/30 text-xs pt-2 text-center">
                Changes take effect immediately for all users
              </p>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4">System Status</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Database Connection</span>
              <span className="text-green-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Connected
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Email Service</span>
              <span className="text-green-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Operational
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">AI Vision API</span>
              <span className="text-green-400 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Operational
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Last Backup</span>
              <span className="text-gray-300">2 hours ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;



